import React , { useState, useEffect, useRef } from 'react';

// Toast
import { toast } from 'react-toastify';

// Axios
import axios from 'axios';

// MUI Components
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';

// Components
import Dropdown from '../form-fields/dropdown';
import ConfirmModal from './confirm-modal';

const EditSiteModal = ({ site, setEditSite }) => {

    const [ open, setOpen ] = useState(false);
    const [ siteOptions, setSiteOptions ] = useState([]);
    const [ updating, setUpdating ] = useState(false);
    const [ confirmDeleteOpen, setConfirmDeleteOpen ] = useState(false);
    const [ deleting, setDeleting ] = useState(false);
    const [ additionalEmails, setAdditionalEmails ] = useState('');
    const [ additionalEmailsError, setAdditionalEmailsError ] = useState('');
    const [ savingEmails, setSavingEmails ] = useState(false);

    // Tracks changes that alter what the table shows, so it only refreshes when needed.
    const tableChanged = useRef(false);

    const handleClose = () => {
        setConfirmDeleteOpen(false);
        setOpen(false);
        setEditSite(null, tableChanged.current);
        tableChanged.current = false;
    }

    const getSites = () => {
        axios.get('/parent-site-options').then(response => {
            setSiteOptions(
                [{ label: 'None', value: null }, ...response.data.options]
            );
        });
    }

    const setStageModeFor = (value) => {
        // setEditSite({ ...site, stage_mode_for: value });
        axios.post(`/site/${site.id}/update`, {
            stage_for: value
        }).then(response => {
            if( response.data.success ){
                toast.success(response.data.message);
                tableChanged.current = true;
                handleClose();
            }
        });
    }

    const updatePlugin = () => {
        if( updating ){
            return;
        }
        setUpdating(true);
        axios.post(`/site/${site.id}/update-plugin`).then(response => {
            if( response.data.success ){
                toast.success(response.data.message);
                if( response.data.site ){
                    setEditSite(response.data.site);
                }
            }
            else{
                toast.error(response.data.message);
            }
        }).catch(() => {
            toast.error('Failed to update plugin');
        }).finally(() => {
            setUpdating(false);
        });
    }

    // Mirrors the API's validation so obvious typos are caught before submitting.
    const validateEmailList = (value) => {
        const emailPattern = /^[^\s@,]+@[^\s@,]+\.[^\s@,]+$/;
        const invalid = value.split(',')
            .map(email => email.trim())
            .filter(email => email !== '' && !emailPattern.test(email));
        return invalid.length ? `Invalid email(s): ${invalid.join(', ')}` : '';
    }

    const saveAdditionalEmails = () => {
        if( savingEmails ){
            return;
        }
        const error = validateEmailList(additionalEmails);
        setAdditionalEmailsError(error);
        if( error ){
            return;
        }
        setSavingEmails(true);
        axios.post(`/site/${site.id}/update-additional-emails`, {
            additional_emails: additionalEmails
        }).then(response => {
            if( response.data.success ){
                toast.success(response.data.message);
                tableChanged.current = true;
                if( response.data.site ){
                    setEditSite(response.data.site);
                }
            }
            else{
                toast.error(response.data.message);
            }
        }).catch((error) => {
            const message = error.response?.data?.message || 'Failed to update additional emails';
            setAdditionalEmailsError(message);
            toast.error(message);
        }).finally(() => {
            setSavingEmails(false);
        });
    }

    const deleteSite = () => {
        if( deleting ){
            return;
        }
        setDeleting(true);
        axios.post(`/site/${site.id}/delete`).then(response => {
            if( response.data.success ){
                toast.success(response.data.message);
                setConfirmDeleteOpen(false);
                tableChanged.current = true;
                handleClose();
            }
            else{
                toast.error(response.data.message);
            }
        }).catch(() => {
            toast.error('Failed to delete site');
        }).finally(() => {
            setDeleting(false);
        });
    }

    useEffect(() => {
        if( site ){
            setOpen(true);
        }
    }, [site]);

    // Reset the field whenever a different site is opened or the saved value changes.
    useEffect(() => {
        setAdditionalEmails(site?.additional_emails || '');
        setAdditionalEmailsError('');
    }, [site?.id, site?.additional_emails]);

    useEffect(() => {
        if( !open ){
            setEditSite(null);
        }
    }, [open]);

    useEffect(() => {
        getSites();
    }, []);

    return (
        <>
            {site && (
                <Dialog className='site-details-modal' open={open} onClose={handleClose} maxWidth='sm' fullWidth>
                    <DialogTitle className='modal-title'>{site.domain}</DialogTitle>
                    <DialogContent>
                        <Box className='site-info'>
                            <p>Currently Installed: {site.is_active ? 'Yes' : 'No'}</p>
                            <p>Email: {site.admin_email || '—'}</p>
                            <p>
                                Plugin Version: {site.plugin_version || 'Unknown'}
                                {' '}
                                <Link
                                    className='plugin-update-link'
                                    component='button'
                                    type='button'
                                    underline='hover'
                                    disabled={updating}
                                    onClick={updatePlugin}
                                >
                                    {updating ? 'Updating…' : 'Update'}
                                </Link>
                            </p>
                            <p>Mode: {site.stage_mode_enabled ? 'Stage' : 'Live'}</p>
                            <p>Last Crawled: {site.last_crawled}</p>
                        </Box>
                        <Link href={`${site.magic_link}`} target='_blank'>Login</Link>
                        <Box className='form'>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mt: 2, mb: 2 }}>
                                <TextField
                                    label='Additional Notification Emails'
                                    name='additional_emails'
                                    size='small'
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    value={additionalEmails}
                                    onChange={(e) => {
                                        setAdditionalEmails(e.target.value);
                                        setAdditionalEmailsError('');
                                    }}
                                    error={!!additionalEmailsError}
                                    helperText={additionalEmailsError || 'Comma separated. These addresses are CC\'d on notification emails sent to this site.'}
                                    disabled={savingEmails}
                                />
                                <Button
                                    variant='contained'
                                    onClick={saveAdditionalEmails}
                                    disabled={savingEmails || additionalEmails === (site.additional_emails || '')}
                                >
                                    {savingEmails ? 'Saving…' : 'Save'}
                                </Button>
                            </Box>
                            {(site.stage_mode_enabled && siteOptions.length > 0) ? (
                                <Dropdown
                                    label='Stage Mode For'
                                    name='stage_for'
                                    options={siteOptions}
                                    value={site.stage_for}
                                    onChange={(e) => setStageModeFor(e.target.value)}
                                />
                            ) : null}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant='contained'
                            color='error'
                            onClick={() => setConfirmDeleteOpen(true)}
                            disabled={deleting}
                            sx={{ mr: 'auto' }}
                        >
                            Delete
                        </Button>
                        <Button variant='contained' color='error' onClick={handleClose}>Close</Button>
                    </DialogActions>
                </Dialog>
            )}
            <ConfirmModal
                open={confirmDeleteOpen}
                setOpen={setConfirmDeleteOpen}
                title='Delete Site'
                message={site ? `Are you sure you want to delete ${site.domain}? This cannot be undone.` : ''}
                onConfirm={deleteSite}
                onCancel={() => setConfirmDeleteOpen(false)}
            />
        </>
    );

}

export default EditSiteModal;